/**
 * Free-shipping bar celebration.
 *
 * The bar's value/state is computed entirely in Liquid and re-rendered
 * server-side on every cart mutation. This element's only job is to fire a
 * one-shot tea-leaf confetti burst when the cart transitions INTO the
 * free-shipping-qualified state.
 *
 * Shared cross-instance state lives on `window.__atcFs` so the drawer instance
 * and the cart-page instance (both present on /cart) cooperate:
 *   - `qualified`       last known qualified boolean
 *   - `celebrateWanted` timestamp set when a transition is detected, cleared
 *                       once a *visible* instance has celebrated
 *
 * Because all instances re-connect synchronously during the section innerHTML
 * swap, the visible instance celebrates even when a hidden instance (e.g. the
 * closed drawer on the cart page) detected the transition first.
 */
(function () {
  const LEAF_SVG =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">' +
    '<path d="M6.05 8.05c-2.73 2.73-2.73 7.15-.02 9.88 1.47-3.4 4.09-6.24 7.36-7.93-2.77 2.34-4.71 5.61-5.39 9.32 2.6 1.23 5.8.78 7.95-1.37C19.43 14.5 20 4 20 4S9.5 4.57 6.05 8.05z"/>' +
    '</svg>';
  const LEAF_COUNT = 18;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const state = (window.__atcFs = window.__atcFs || {});
      const qualified = this.dataset.qualified === 'true';
      const prev = state.qualified;

      state.qualified = qualified;

      // First render this session: record the baseline, never celebrate on load.
      if (typeof prev === 'undefined') return;

      if (qualified && !prev) {
        state.celebrateWanted = Date.now();
      }

      const wanted = state.celebrateWanted;
      const fresh = wanted && Date.now() - wanted < 1000;

      if (
        fresh &&
        this.dataset.confetti === 'true' &&
        !prefersReducedMotion() &&
        this.isVisible()
      ) {
        state.celebrateWanted = 0;
        this.celebrate();
      }
    }

    isVisible() {
      // offsetParent is null when an ancestor is display:none (e.g. closed drawer).
      return this.offsetParent !== null;
    }

    celebrate() {
      const host = this.querySelector('.m-fs-bar__confetti') || this;

      for (let i = 0; i < LEAF_COUNT; i++) {
        const leaf = document.createElement('span');
        leaf.className = 'm-fs-bar__leaf';
        leaf.style.setProperty('--x', (Math.random() * 100).toFixed(2) + '%');
        leaf.style.setProperty('--delay', (Math.random() * 0.35).toFixed(2) + 's');
        leaf.style.setProperty('--dur', (1 + Math.random() * 0.8).toFixed(2) + 's');
        leaf.style.setProperty('--rot', Math.floor(Math.random() * 540 - 180) + 'deg');
        leaf.style.setProperty('--drift', Math.floor(Math.random() * 60 - 30) + 'px');
        leaf.innerHTML = LEAF_SVG;
        leaf.addEventListener('animationend', () => leaf.remove());
        host.appendChild(leaf);
      }

      // Safety cleanup in case animationend doesn't fire (reduced motion, etc.)
      setTimeout(() => {
        host.querySelectorAll('.m-fs-bar__leaf').forEach((el) => el.remove());
      }, 2600);
    }
  }

  if (!customElements.get('free-shipping-bar')) {
    customElements.define('free-shipping-bar', FreeShippingBar);
  }
})();
